import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Transactions & Fees',
    tagline: 'Three transaction types, one shared job: reach __validate__ honestly.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - the three Starknet transaction types and what each is for
//   Q1 - why a nonce matters for every account-abstracted transaction
export const blocks: StageBlock[] = [
    content('hero', () => (
        <StageSection gradientBorder eyebrow="Stage 5 of 8" title="Three kinds of transactions">
            <Prose>
                Because every account is a contract that must exist before it can act, and every contract's code
                must exist before it can be deployed, Starknet defines three transaction types instead of Ethereum's
                one general-purpose transaction:
            </Prose>
            <FactGrid items={[
                ['DECLARE', 'Publishes a compiled contract class onto Starknet, identified by its class hash, so it can later be deployed (possibly many times).'],
                ['DEPLOY_ACCOUNT', 'Creates a new account contract instance from an already-declared class, the special-cased transaction that lets a brand-new account pay for its own first appearance on-chain.'],
                ['INVOKE', 'The everyday transaction: calls an entrypoint on an already-deployed contract, almost always routed through the sender account\'s __execute__.'],
            ]} />
        </StageSection>
    )),

    question(0),

    content('nonces-and-replay', () => (
        <StageSection title="Nonces still do the job you'd expect">
            <Prose>
                Every account tracks its own nonce, incremented with each transaction it sends. <code>__validate__</code>{' '}
                checks the incoming transaction's nonce against the account's current value, which prevents the same
                signed transaction from being replayed and enforces an ordering on transactions from the same
                account, the same purpose a nonce serves for an Ethereum EOA, just checked by account code instead
                of protocol code.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('fees', () => (
        <StageSection title="Fees: paid by the account, not a separate concept">
            <Prose>
                Transaction fees on Starknet are paid in the network's fee token by the sending account itself, as
                part of that account's own <code>__validate__</code>/<code>__execute__</code> flow rather than a
                protocol-level deduction from a plain address balance. That's what makes fee sponsorship, paymasters,
                and "gasless" transactions for users possible without any special protocol support: the account
                contract can simply choose to let something else cover its own fee.
            </Prose>
            <KeyPoints items={[
                <>A transaction that reverts during __execute__ is still included in a block and still charged a fee, because Starknet must be able to prove that the revert itself occurred correctly.</>,
                <>Fee logic living in account code means a bug there can be a denial-of-service or griefing vector just as easily as a signature bug can; both are covered in the Cairo security track ahead.</>,
            ]} />
        </StageSection>
    )),
];
